
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { 
  Briefcase, 
  Building, 
  Mail, 
  MapPin,
  GraduationCap,
  CircleUser,
  Phone
} from "lucide-react";

interface ProfileTabProps {
  candidate: {
    full_name: string;
    email: string;
    location?: string;
    job_title?: string;
    industry?: string;
    avatar_url?: string;
    organization?: string;
    phone_number?: string;
    calendly_url?: string;
  };
}

export function ProfileTab({ candidate }: ProfileTabProps) {
  // Get first letter of full name for avatar fallback
  const getInitials = () => {
    return candidate?.full_name?.charAt(0) || "C";
  };

  return (
    <div className="space-y-8">
      <Card className="shadow-sm border">
        <CardHeader className="pb-3 bg-card/50">
          <CardTitle className="text-lg font-semibold">Candidate Profile</CardTitle>
        </CardHeader>
        <CardContent className="pt-4 p-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex flex-col items-center md:items-start gap-3">
              <Avatar className="h-24 w-24">
                {candidate.avatar_url && (
                  <AvatarImage src={candidate.avatar_url} alt={candidate.full_name} />
                )}
                <AvatarFallback className="text-2xl">{getInitials()}</AvatarFallback>
              </Avatar>
            </div>
            
            <div className="flex-1 space-y-4">
              <div>
                <h3 className="text-xl font-bold">{candidate.full_name}</h3>
                {candidate.job_title && (
                  <p className="text-muted-foreground">{candidate.job_title}</p>
                )}
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>{candidate.email}</span>
                </div>
                
                {candidate.phone_number && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{candidate.phone_number}</span>
                  </div>
                )}
                
                {candidate.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{candidate.location}</span>
                  </div>
                )}
                
                {candidate.organization && (
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    <span>{candidate.organization}</span>
                  </div>
                )}
                
                {candidate.industry && (
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                    <span>{candidate.industry}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <Separator className="my-6" />
          
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-muted-foreground">Background</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h5 className="text-sm font-medium flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Professional Experience
                </h5>
                <div className="text-sm text-muted-foreground">
                  {candidate.job_title ? (
                    <p>Currently working as {candidate.job_title}</p>
                  ) : (
                    <p>No professional experience information available.</p>
                  )}
                </div>
              </div>
              
              <div className="space-y-3">
                <h5 className="text-sm font-medium flex items-center gap-2">
                  <GraduationCap className="h-4 w-4" />
                  Education
                </h5>
                <div className="text-sm text-muted-foreground">
                  <p>No education information available.</p>
                </div>
              </div>
            </div>
          </div>
          
          <Separator className="my-6" />
          
          <div className="space-y-3">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <CircleUser className="h-4 w-4" />
              About
            </h4>
            <div className="text-sm text-muted-foreground">
              <p>No additional information available for this candidate.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
